
import { useState } from 'react';

export default function Module3() {
  const [activeSection, setActiveSection] = useState<number | null>(null);

  const sections = [
    {
      id: 1,
      title: "什么是市场采购贸易（1039）？",
      subtitle: "政策背景与核心定义",
      icon: "ri-shopping-bag-3-line",
      content: {
        background: "为解决\"天健商业广场\"等专业市场\"多品种、多批次、小批量\"交易方式出口难、结汇难、免税难的问题而首创的政策",
        definition: "在经认定的市场集聚区内采购的、单票报关单货值不超过15万美元的商品",
        keywords: [
          {
            term: "认定市场",
            explanation: "如广东天健商业广场等经国家批准的试点市场",
            importance: "必须在试点区域内操作"
          },
          {
            term: "免征增值税",
            explanation: "出口环节无需缴纳增值税，降低成本",
            importance: "解决无票货物合法出口问题"
          },
          {
            term: "不退税",
            explanation: "不享受出口退税政策，但免征更直接",
            importance: "简化流程，快速通关"
          }
        ],
        timeline: [
          { year: "2013年", event: "天健商业广场率先试点市场采购贸易方式" },
          { year: "2016年", event: "扩大试点范围，多个城市获批" }
        ]
      }
    },
    {
      id: 2,
      title: "核心优势与劣势",
      subtitle: "全面对比分析",
      icon: "ri-scales-3-line",
      content: {
        advantages: [
          {
            category: "出口环节",
            title: "免征增值税优势",
            description: "彻底解决\"无票货物\"出口合法性问题",
            details: [
              "无需开具增值税发票",
              "不用办理出口退税手续",
              "简化财务处理流程",
              "降低合规成本"
            ],
            impact: "成本降低5-8%，流程简化70%"
          },
          {
            category: "通关环节",
            title: "简化申报模式",
            description: "允许按\"章目\"归并申报，极大提升通关效率",
            details: [
              "可按大类申报（如\"塑料制品\"）",
              "无需逐项详细申报",
              "减少通关时间",
              "降低申报错误风险"
            ],
            impact: "通关效率提升60%，申报时间减少80%"
          },
          {
            category: "收汇环节",
            title: "灵活收汇模式",
            description: "突破\"谁出口，谁收汇\"的限制，收汇更灵活",
            details: [
              "可由代理公司统一收汇",
              "接受境外个人账户汇款",
              "适合跨境电商收款",
              "结汇流程更便捷"
            ],
            impact: "收汇效率提升40%，适用场景扩大300%"
          }
        ],
        disadvantages: [
          {
            type: "地域限制",
            description: "必须在国家批准的试点市场内注册备案",
            challenges: [
              "试点市场数量有限",
              "需要在指定区域设立主体",
              "增加企业运营成本",
              "限制业务发展范围"
            ],
            solutions: [
              "选择合适的试点市场",
              "与当地代理公司合作",
              "评估设立成本效益"
            ]
          },
          {
            type: "货值限制", 
            description: "单票货值不能超过15万美元",
            challenges: [
              "大额订单需要拆分",
              "增加操作复杂度",
              "可能影响运输效率",
              "客户接受度问题"
            ],
            solutions: [
              "合理拆分大额订单",
              "优化装箱和运输方案",
              "与客户充分沟通"
            ]
          },
          {
            type: "贸易方式限制",
            description: "主要适用于消费品，不适合所有业务类型",
            challenges: [
              "不适合需要退税的业务",
              "不适合高科技产品",
              "监管要求相对严格",
              "品类范围有限制"
            ],
            solutions: [
              "准确评估产品适用性",
              "选择合适的贸易方式",
              "做好合规风险防控"
            ]
          },
          {
            type: "侵权风险",
            description: "品类杂需严防假冒伪劣，海关监管严格",
            challenges: [
              "知识产权风险高",
              "海关查验严格",
              "违规成本巨大",
              "影响企业信誉"
            ],
            solutions: [
              "建立严格的产品筛查机制",
              "加强供应商管理",
              "定期进行合规培训"
            ]
          }
        ]
      }
    },
    {
      id: 3,
      title: "业务流程详解",
      subtitle: "代理出口操作实务",
      icon: "ri-flow-chart",
      content: {
        flowSteps: [
          {
            step: "供应商供货",
            description: "无票供货商提供商品",
            participants: ["供应商", "采购商"],
            keyPoints: [
              "商品必须符合出口标准",
              "提供商品详细信息",
              "确保商品合法合规",
              "建立供应商档案"
            ],
            documents: ["商品清单", "质量证书", "供应商资质"],
            duration: "1-3天"
          },
          {
            step: "1039备案主体接单",
            description: "试点市场内备案企业接收订单",
            participants: ["1039备案企业", "客户"],
            keyPoints: [
              "核实备案资质",
              "确认商品适用性",
              "签订代理协议",
              "制定出口方案"
            ],
            documents: ["代理协议", "备案证明", "出口合同"],
            duration: "1-2天"
          },
          {
            step: "组货装箱",
            description: "将多个供应商货物组合装箱",
            participants: ["仓储企业", "1039企业"],
            keyPoints: [
              "合理配载组合",
              "优化装箱方案",
              "确保货物安全",
              "制作装箱清单"
            ],
            documents: ["装箱单", "仓储记录", "货物照片"],
            duration: "2-5天"
          },
          {
            step: "联网信息平台录入",
            description: "在1039专用平台录入完整信息",
            participants: ["1039企业", "监管部门"],
            keyPoints: [
              "商品信息准确录入",
              "交易信息完整记录",
              "供货商信息备案",
              "确保数据真实性"
            ],
            documents: ["平台录入单", "交易凭证", "供应商信息"],
            duration: "1天",
            highlight: "这是1039合规的核心环节"
          },
          {
            step: "报关出口",
            description: "使用\"1039\"代码向海关申报",
            participants: ["报关行", "海关"],
            keyPoints: [
              "使用正确贸易方式代码",
              "按章目归并申报",
              "配合海关查验",
              "确保单证一致"
            ],
            documents: ["报关单", "发票", "装箱单", "合同"],
            duration: "1-2天"
          },
          {
            step: "代理收汇",
            description: "代理公司统一收取外汇",
            participants: ["代理公司", "银行", "客户"],
            keyPoints: [
              "核实收汇金额",
              "办理结汇手续",
              "记录交易流水",
              "生成收汇凭证"
            ],
            documents: ["银行水单", "结汇凭证", "收汇通知"],
            duration: "5-15天"
          },
          {
            step: "资金结算",
            description: "扣除费用后支付给供应商",
            participants: ["代理公司", "供应商"],
            keyPoints: [
              "核算代理费用",
              "扣除相关税费",
              "及时支付货款",
              "提供结算清单"
            ],
            documents: ["结算清单", "付款凭证", "费用明细"],
            duration: "3-7天"
          }
        ],
        platformImportance: {
          title: "联网信息平台的重要性",
          description: "1039合规的核心，用于税务、外汇、海关等部门进行信息校验和监管",
          functions: [
            "交易信息实时记录",
            "多部门数据共享",
            "风险预警监控", 
            "合规性自动检查"
          ],
          benefits: [
            "提高通关效率",
            "降低合规风险",
            "简化申报流程",
            "增强监管透明度"
          ]
        }
      }
    },
    {
      id: 4,
      title: "适用场景分析",
      subtitle: "精准匹配业务需求",
      icon: "ri-target-line",
      content: {
        scenarios: [
          {
            type: "中小微企业及个体工商户",
            description: "无法取得进项发票的小规模经营者",
            characteristics: [
              "采购渠道分散，难以取得正规发票",
              "业务规模较小，单票金额不大",
              "财务管理相对简单",
              "对成本敏感度较高"
            ],
            advantages: [
              "解决无票货物出口难题",
              "免征增值税降低成本",
              "简化财务处理流程",
              "提高资金周转效率"
            ],
            examples: "小商品批发商、个体贸易商、创业型企业等",
            successCase: "广州某小商品贸易商，年出口额从50万增长到300万美元"
          },
          {
            type: "跨境电商卖家",
            description: "B2C/B2小B模式的跨境电商企业",
            characteristics: [
              "通过海外仓或小包发货",
              "订单量大但单票金额小",
              "客户分散在多个国家",
              "需要灵活的收汇方式"
            ],
            advantages: [
              "支持境外个人账户收汇",
              "适合小批量多批次出口",
              "简化通关申报程序",
              "降低合规运营成本"
            ],
            examples: "Amazon、eBay、速卖通等平台卖家",
            successCase: "深圳某跨境电商，通过1039方式年出口额突破1000万美元"
          },
          {
            type: "专业市场商户",
            description: "在各类专业批发市场经营的商户",
            characteristics: [
              "商品种类繁多",
              "供应链条较短",
              "客户以批发为主",
              "对价格竞争力要求高"
            ],
            advantages: [
              "享受试点市场政策优惠",
              "就近办理出口手续",
              "利用市场集聚效应",
              "降低物流运营成本"
            ],
            examples: "广东天健商业广场、广州白马服装城商户等",
            successCase: "天健商业广场某玩具商户，出口效率提升60%，成本降低8%"
          },
          {
            type: "特殊货物出口",
            description: "货样、广告品等低值货物的出口",
            characteristics: [
              "货值较低，通常单票不足万元",
              "品种杂，数量少",
              "时效要求较高",
              "传统方式成本过高"
            ],
            advantages: [
              "简化申报降低成本",
              "提高通关效率",
              "减少单证准备工作",
              "适合小额高频出口"
            ],
            examples: "样品出口、展会物料、广告赠品等",
            successCase: "某展览公司，展会物料出口时间缩短70%"
          }
        ],
        decisionMatrix: {
          title: "1039适用性评估矩阵",
          criteria: [
            {
              factor: "发票获取难度",
              weight: "25%",
              lowScore: "容易取得增值税专用发票",
              highScore: "难以获得正规发票",
              recommendation: "高分更适合1039"
            },
            {
              factor: "单票货值水平", 
              weight: "20%",
              lowScore: "经常超过15万美元",
              highScore: "通常低于10万美元",
              recommendation: "高分更适合1039"
            },
            {
              factor: "商品品类复杂度",
              weight: "20%",
              lowScore: "品类单一，标准化程度高",
              highScore: "品类繁多，规格复杂",
              recommendation: "高分更适合1039"
            },
            {
              factor: "退税需求强度",
              weight: "20%",
              lowScore: "强烈需要出口退税",
              highScore: "对退税需求不强",
              recommendation: "高分更适合1039"
            },
            {
              factor: "合规管理能力",
              weight: "15%",
              lowScore: "合规体系完善",
              highScore: "合规管理相对薄弱",
              recommendation: "高分更适合1039"
            }
          ]
        }
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <i className="ri-graduation-cap-line text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Pacifico, serif" }}>天健外贸培训学院</h1>
                <p className="text-sm text-gray-600">专业外贸知识培训平台</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                返回首页
              </button>
              <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                立即报名
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/29c26e1b01894392b2acd1560510d187.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/90 to-pink-900/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-3 mb-4">
              <span className="bg-purple-500 text-white px-4 py-2 rounded-full text-sm font-medium">模块三</span>
              <span className="bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium">1.5小时</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-4 leading-tight">
              市场采购贸易方式解析
            </h1>
            <p className="text-xl text-purple-100 mb-6">
              新业态利器 · 让学员理解市场采购贸易如何解决中小微企业出口难题，并掌握其操作模式和限制
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-white mb-3">培训目标：</h3>
              <p className="text-purple-100 leading-relaxed">
                通过本模块学习，学员将全面掌握市场采购贸易（1039）的政策背景、核心优势、操作流程，
                并能准确判断企业是否适合采用此种贸易方式，有效解决中小微企业出口难题。
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Course Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">详细内容</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            四大核心主题，深度解析市场采购贸易新业态
          </p>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={section.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div 
                className="bg-gradient-to-r from-purple-600 to-purple-700 p-6 cursor-pointer"
                onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <i className={`${section.icon} text-white text-xl`}></i>
                    </div>
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm font-medium">
                          主题{index + 1}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-1">{section.title}</h3>
                      <p className="text-white/80 text-lg">{section.subtitle}</p>
                    </div>
                  </div>
                  <div className="text-white">
                    <i className={`${activeSection === section.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl`}></i>
                  </div>
                </div>
              </div>
              
              {activeSection === section.id && (
                <div className="p-8">
                  {/* Section 1: 什么是市场采购贸易（1039）？ */}
                  {section.id === 1 && (
                    <div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-8 text-white">
                          <h4 className="text-2xl font-bold mb-6">
                            <i className="ri-government-line mr-3"></i>
                            政策背景
                          </h4>
                          <p className="text-lg leading-relaxed mb-6">{section.content.background}</p>
                          <div className="bg-white/10 rounded-lg p-4">
                            <h5 className="font-semibold mb-3">核心定义</h5>
                            <p className="text-purple-100">{section.content.definition}</p>
                          </div>
                        </div>
                        
                        <div className="bg-gray-50 rounded-2xl p-8">
                          <h4 className="text-2xl font-bold text-gray-900 mb-6">
                            <i className="ri-key-line mr-3"></i>
                            关键词解析
                          </h4>
                          <div className="space-y-4">
                            {section.content.keywords.map((keyword, idx) => (
                              <div key={idx} className="border-l-4 border-purple-500 pl-4">
                                <h5 className="font-bold text-purple-900 mb-2">{keyword.term}</h5>
                                <p className="text-gray-700 text-sm mb-2">{keyword.explanation}</p>
                                <p className="text-purple-600 text-xs font-medium">{keyword.importance}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
                        <h4 className="text-2xl font-bold text-gray-900 mb-6">
                          <i className="ri-timeline-line mr-3"></i>
                          政策发展历程
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                          {section.content.timeline.map((item, idx) => (
                            <div key={idx} className="relative">
                              <div className="bg-white rounded-xl p-6 shadow-sm">
                                <div className="text-2xl font-bold text-purple-600 mb-2">{item.year}</div>
                                <p className="text-gray-700 text-sm leading-relaxed">{item.event}</p>
                              </div>
                              {idx < section.content.timeline.length - 1 && (
                                <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                                  <div className="w-6 h-6 bg-purple-200 rounded-full flex items-center justify-center">
                                    <i className="ri-arrow-right-line text-purple-600 text-sm"></i>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 2: 核心优势与劣势 */}
                  {section.id === 2 && (
                    <div>
                      <div className="mb-12">
                        <h4 className="text-2xl font-bold text-gray-900 mb-8">
                          <i className="ri-trophy-line mr-3 text-green-600"></i>
                          核心优势
                        </h4>
                        <div className="space-y-8">
                          {section.content.advantages.map((advantage, idx) => (
                            <div key={idx} className="bg-green-50 rounded-2xl p-8 border border-green-200">
                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div className="lg:col-span-1">
                                  <div className="bg-green-500 text-white rounded-xl p-6">
                                    <h5 className="text-xl font-bold mb-3">{advantage.category}</h5>
                                    <h6 className="text-lg font-semibold mb-3">{advantage.title}</h6>
                                    <p className="text-green-100 text-sm">{advantage.description}</p>
                                    <div className="mt-4 bg-green-400/20 rounded-lg p-3">
                                      <p className="text-green-100 text-xs font-medium">{advantage.impact}</p>
                                    </div>
                                  </div>
                                </div>
                                <div className="lg:col-span-2">
                                  <h6 className="font-bold text-gray-900 mb-4">具体优势表现</h6>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {advantage.details.map((detail, detailIdx) => (
                                      <div key={detailIdx} className="bg-white rounded-lg p-4 border border-green-200">
                                        <div className="flex items-start space-x-3">
                                          <i className="ri-check-double-line text-green-500 mt-1"></i>
                                          <p className="text-gray-700 text-sm">{detail}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-2xl font-bold text-gray-900 mb-8">
                          <i className="ri-alert-line mr-3 text-red-600"></i>
                          核心劣势与限制
                        </h4>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {section.content.disadvantages.map((disadvantage, idx) => (
                            <div key={idx} className="bg-red-50 rounded-2xl p-6 border border-red-200">
                              <h5 className="text-xl font-bold text-red-900 mb-4">{disadvantage.type}</h5>
                              <p className="text-red-800 mb-6">{disadvantage.description}</p>
                              
                              <div className="space-y-4">
                                <div>
                                  <h6 className="font-semibold text-gray-900 mb-3">主要挑战</h6>
                                  <div className="space-y-2">
                                    {disadvantage.challenges.map((challenge, challengeIdx) => (
                                      <div key={challengeIdx} className="flex items-start space-x-2">
                                        <i className="ri-error-warning-line text-red-500 mt-1 text-sm"></i>
                                        <p className="text-gray-700 text-sm">{challenge}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                
                                <div className="bg-blue-100 rounded-lg p-4">
                                  <h6 className="font-semibold text-blue-900 mb-2">应对方案</h6>
                                  <div className="space-y-1">
                                    {disadvantage.solutions.map((solution, solutionIdx) => (
                                      <div key={solutionIdx} className="flex items-start space-x-2">
                                        <i className="ri-lightbulb-line text-blue-600 mt-1 text-sm"></i>
                                        <p className="text-blue-800 text-sm">{solution}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 3: 业务流程详解 */}
                  {section.id === 3 && (
                    <div>
                      <div className="mb-8">
                        <h4 className="text-2xl font-bold text-gray-900 mb-6">
                          <i className="ri-route-line mr-3 text-blue-600"></i>
                          完整业务流程（代理出口）
                        </h4>
                        <div className="space-y-4">
                          {section.content.flowSteps.map((step, idx) => (
                            <div key={idx} className="relative">
                              <div className={`bg-white border-2 rounded-xl p-6 hover:shadow-lg transition-shadow ${step.highlight ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200'}`}>
                                <div className="flex items-start space-x-4">
                                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg flex-shrink-0 ${step.highlight ? 'bg-yellow-500 text-white' : 'bg-purple-500 text-white'}`}>
                                    {idx + 1}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between mb-3">
                                      <h5 className="text-xl font-bold text-gray-900">{step.step}</h5>
                                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                                        <span className="bg-gray-100 px-3 py-1 rounded-full">{step.duration}</span>
                                        {step.highlight && <span className="bg-yellow-200 text-yellow-800 px-3 py-1 rounded-full font-medium">核心环节</span>}
                                      </div>
                                    </div>
                                    <p className="text-gray-700 mb-4">{step.description}</p>
                                    
                                    <div className="mb-4">
                                      <h6 className="font-semibold text-gray-900 mb-2">参与方</h6>
                                      <div className="flex flex-wrap gap-2">
                                        {step.participants.map((participant, pIdx) => (
                                          <span key={pIdx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                                            {participant}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <h6 className="font-semibold text-gray-900 mb-2">关键要点</h6>
                                        <ul className="space-y-1">
                                          {step.keyPoints.map((point, pointIdx) => (
                                            <li key={pointIdx} className="flex items-start text-sm text-gray-700">
                                              <i className="ri-check-line mr-2 text-green-500 mt-0.5"></i>
                                              {point}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                      <div>
                                        <h6 className="font-semibold text-gray-900 mb-2">相关单据</h6>
                                        <ul className="space-y-1">
                                          {step.documents.map((doc, docIdx) => (
                                            <li key={docIdx} className="flex items-center text-sm text-gray-700">
                                              <i className="ri-file-text-line mr-2 text-purple-500"></i>
                                              {doc}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {idx < section.content.flowSteps.length - 1 && (
                                <div className="flex justify-center my-2">
                                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                    <i className="ri-arrow-down-line text-gray-600"></i>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-8 border border-yellow-200">
                        <h4 className="text-2xl font-bold text-gray-900 mb-6">
                          <i className="ri-global-line mr-3 text-yellow-600"></i>
                          {section.content.platformImportance.title}
                        </h4>
                        <p className="text-lg text-gray-700 mb-6">{section.content.platformImportance.description}</p>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          <div>
                            <h5 className="font-bold text-gray-900 mb-4">核心功能</h5>
                            <div className="space-y-3">
                              {section.content.platformImportance.functions.map((func, idx) => (
                                <div key={idx} className="flex items-center space-x-3">
                                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                  <p className="text-gray-700">{func}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h5 className="font-bold text-gray-900 mb-4">主要效益</h5>
                            <div className="space-y-3">
                              {section.content.platformImportance.benefits.map((benefit, idx) => (
                                <div key={idx} className="flex items-center space-x-3">
                                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                  <p className="text-gray-700">{benefit}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 4: 适用场景分析 */}
                  {section.id === 4 && (
                    <div>
                      <div className="mb-12">
                        <h4 className="text-2xl font-bold text-gray-900 mb-8">
                          <i className="ri-user-settings-line mr-3 text-purple-600"></i>
                          适用场景详解
                        </h4>
                        <div className="space-y-8">
                          {section.content.scenarios.map((scenario, idx) => (
                            <div key={idx} className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div className="lg:col-span-1">
                                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl p-6">
                                    <h5 className="text-xl font-bold mb-3">{scenario.type}</h5>
                                    <p className="text-purple-100 text-sm leading-relaxed">{scenario.description}</p>
                                  </div>
                                </div>
                                
                                <div className="lg:col-span-2">
                                  <div className="space-y-6">
                                    <div>
                                      <h6 className="font-bold text-gray-900 mb-3">典型特征</h6>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {scenario.characteristics.map((char, charIdx) => (
                                          <div key={charIdx} className="flex items-start space-x-2">
                                            <i className="ri-record-circle-line text-blue-500 mt-1 text-sm"></i>
                                            <p className="text-gray-700 text-sm">{char}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    
                                    <div>
                                      <h6 className="font-bold text-gray-900 mb-3">主要优势</h6>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {scenario.advantages.map((adv, advIdx) => (
                                          <div key={advIdx} className="flex items-start space-x-2">
                                            <i className="ri-star-line text-green-500 mt-1 text-sm"></i>
                                            <p className="text-gray-700 text-sm">{adv}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="bg-gray-50 rounded-lg p-4">
                                        <h6 className="font-semibold text-gray-900 mb-2 text-sm">典型案例</h6>
                                        <p className="text-gray-700 text-sm">{scenario.examples}</p>
                                      </div>
                                      <div className="bg-green-50 rounded-lg p-4">
                                        <h6 className="font-semibold text-green-900 mb-2 text-sm">成功案例</h6>
                                        <p className="text-green-800 text-sm">{scenario.successCase}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8">
                        <h4 className="text-2xl font-bold text-gray-900 mb-8">
                          <i className="ri-bar-chart-box-line mr-3 text-indigo-600"></i>
                          {section.content.decisionMatrix.title}
                        </h4>
                        <div className="space-y-6">
                          {section.content.decisionMatrix.criteria.map((criterion, idx) => (
                            <div key={idx} className="bg-white rounded-xl p-6 border border-indigo-200">
                              <div className="flex items-center justify-between mb-4">
                                <h5 className="text-lg font-bold text-gray-900">{criterion.factor}</h5>
                                <div className="flex items-center space-x-2">
                                  <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
                                    权重: {criterion.weight}
                                  </span>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                <div className="bg-red-50 rounded-lg p-4">
                                  <h6 className="font-semibold text-red-900 mb-2 text-sm">低分情况</h6>
                                  <p className="text-red-800 text-sm">{criterion.lowScore}</p>
                                </div>
                                <div className="bg-green-50 rounded-lg p-4">
                                  <h6 className="font-semibold text-green-900 mb-2 text-sm">高分情况</h6>
                                  <p className="text-green-800 text-sm">{criterion.highScore}</p>
                                </div>
                                <div className="bg-blue-50 rounded-lg p-4">
                                  <h6 className="font-semibold text-blue-900 mb-2 text-sm">推荐建议</h6>
                                  <p className="text-blue-800 text-sm font-medium">{criterion.recommendation}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 py-16">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl font-bold text-white mb-4">
            掌握市场采购贸易新机遇
          </h2>
          <p className="text-xl text-purple-100 mb-8 leading-relaxed">
            解决中小微企业出口难题，开启外贸新业态，提升竞争优势
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="px-8 py-4 bg-white text-purple-600 rounded-lg hover:bg-gray-50 transition-colors font-semibold whitespace-nowrap cursor-pointer">
              <i className="ri-user-add-line mr-2"></i>
              立即报名学习
            </button>
            <button className="px-8 py-4 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors backdrop-blur-sm font-semibold whitespace-nowrap cursor-pointer">
              <i className="ri-download-line mr-2"></i>
              下载1039指南
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <i className="ri-graduation-cap-line text-white"></i>
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: "Pacifico, serif" }}>天健外贸培训学院</h3>
              </div>
              <p className="text-gray-400 mb-4">专业的外贸知识培训平台，助您成为外贸专家</p>
              <div className="flex space-x-4">
                <i className="ri-wechat-line text-xl text-gray-400 hover:text-white cursor-pointer transition-colors"></i>
                <i className="ri-qq-line text-xl text-gray-400 hover:text-white cursor-pointer transition-colors"></i>
                <i className="ri-mail-line text-xl text-gray-400 hover:text-white cursor-pointer transition-colors"></i>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">课程内容</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">外贸基础入门</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">一般贸易解析</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">市场采购贸易</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">风险防控</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">学习支持</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">在线答疑</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">实战案例</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">学习资料</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">证书认证</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">快速链接</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">关于我们</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">联系方式</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">服务条款</a></li>
                <li><a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Made with Readdy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 天健外贸培训学院. 保留所有权利.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
